import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Row,
  Accordion,
  AccordionItem,
  Collapse,
  Spinner,
} from "reactstrap";

import { Autocomplete } from "@react-google-maps/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { limpiarDatosSolicitud } from "../../../utils/solicitudDataCleaner";
import { tipoEfectivo } from "../../../utils/validaciones";
import {
  editSolicitud,
  createMessage,
} from "../../../slices/thunks";
import { solicitudSuccess } from "../../../slices/configuracion/solicitudes/reducer";
import { verificarDireccionSolicitud } from "../../../helpers/backend_helper";
import { useDispatch } from "react-redux";
import GoogleMapWrapper from "../../../Components/Common/GoogleMapWrapper";
import { DatosSesion } from "../../../Components/Common/DatosSesion";
import {
  loadGoogleMapsApi,
  isGoogleMapsLoaded,
  waitForGoogleMapsToLoad,
} from "../../../Components/Common/GoogleMapLoader";
import {
  getPlantas,
  asignarSolicitudAPlanta,
  removerPlantaDeSolicitud,
} from "../../../helpers/backend_helper";

const InformacionDelivery = ({ solicitud, farmacias }) => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificandoDireccion, setVerificandoDireccion] = useState(false);
  const [mostrarSeccionActualizar, setMostrarSeccionActualizar] = useState(false);

  // Obtener datos del usuario logueado (nombre completo)
  const nombreUsuario = `${DatosSesion("nombre")} ${DatosSesion("apellido")}`.trim();

  // Verificar si la dirección ya está verificada
  const direccionVerificada = solicitud?.verificacionDireccion?.verificado === true;
  const verificadoPor = solicitud?.verificacionDireccion?.verificadoPor;
  const fechaVerificacionDireccion = solicitud?.verificacionDireccion?.fecha;

  // Verificar pasos previos completados
  const documentosVerificados = solicitud?.verificacionDocumentos?.verificado === true;
  // Para Latina Salud: verificar diagnóstico pre-asignado
// Para otras aseguradoras: verificar códigoDiagnosticoFarmacologico
const tieneDiagnostico = (() => {
  const aseg = solicitud?.aseguradora?.toLowerCase();
  if (aseg === "latina salud" || solicitud?.aseguradora === "Seguros Mercantil") {
    return solicitud.diagnostico && solicitud.diagnostico.trim() !== "";
  }
  if (aseg === "hispana de seguros" || aseg === "real seguros") {
    // Diagnóstico pre-asignado o diagnóstico farmacológico registrado en-app
    return (solicitud.diagnostico && solicitud.diagnostico.trim() !== "") ||
           !!solicitud?.codigoDiagnosticoFarmacologico;
  }
  return !!solicitud?.codigoDiagnosticoFarmacologico;
})();
  
  const tipoOp = tipoEfectivo(solicitud);
  
  const tieneMedicamentos = tipoOp === "Cronica" 
    ? (() => {
        if (!solicitud.cronico?.historialEntregas?.length) return false;
        
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        
        return solicitud.cronico.historialEntregas.some((entrega) => {
          const entregaDate = new Date(entrega.fecha);
          return (
            entregaDate.getMonth() === currentMonth &&
            entregaDate.getFullYear() === currentYear &&
            entrega.medicamentos?.length > 0
          );
        });
      })()
    : solicitud?.medicamentos && solicitud.medicamentos.length > 0;
  
  const tieneFactura = solicitud?.facturacion && solicitud.facturacion.length > 0;
  const permisoExcedentePendiente = solicitud?.facturacion?.some(f => f.permisoExcedente?.pendiente === true);
  
  const puedeVerificarDireccion = tipoOp === "Cronica" 
    ? (documentosVerificados && tieneMedicamentos && tieneFactura && !permisoExcedentePendiente)
    : (documentosVerificados && tieneDiagnostico && tieneFactura && !permisoExcedentePendiente);

  // Formatear fecha de verificación (formato Venezuela: DD/MM/YYYY HH:MM am/pm)
  const formatearFechaVerificacion = (fecha) => {
    if (!fecha) return '';
    try {
      // Asegurar que la fecha se interprete como UTC
      // Si no tiene 'Z' al final, agregarla para indicar UTC
      let fechaStr = fecha;
      if (typeof fecha === 'string' && !fecha.endsWith('Z') && !fecha.includes('+')) {
        fechaStr = fecha + 'Z';
      }
      
      const date = new Date(fechaStr);
      const dia = date.toLocaleDateString('es-VE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Caracas'
      });
      const hora = date.toLocaleTimeString('es-VE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Caracas'
      });
      return `${dia} ${hora}`;
    } catch {
      return fecha;
    }
  };

  // Handler para verificar dirección
  const handleVerificarDireccion = async () => {
    if (!nombreUsuario) {
      toast.error("No se pudo obtener el usuario actual");
      return;
    }

    if (!solicitud?.coordenadas || solicitud.coordenadas.length !== 2) {
      toast.error("Debe registrar las coordenadas antes de verificar la dirección");
      return;
    }

    setVerificandoDireccion(true);
    try {
      // Usar endpoint específico para verificar dirección
      const verificacion = {
        verificado: true,
        verificadoPor: nombreUsuario,
        fecha: new Date().toISOString()
      };

      const response = await verificarDireccionSolicitud(solicitud.id, verificacion);
      
      // Actualizar estado local con la solicitud actualizada
      if (response && response.data) {
        // El reducer espera: action.payload.data.data
        dispatch(solicitudSuccess({
          data: {
            data: response.data.data || response.data
          },
          status: 200
        }));
      }
      
      toast.success("✓ Dirección verificada correctamente", {
        position: "top-right",
        autoClose: 3000
      });
    } catch (error) {
      console.error("Error al verificar dirección:", error);
      toast.error("Error al verificar dirección");
    } finally {
      setVerificandoDireccion(false);
    }
  };
  const [nuevaDireccion, setNuevaDireccion] = useState({
    direccion: "",
    coordenadas: [],
  });
  const [searchBox, setSearchBox] = useState(null);
  const [map, setMap] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [center, setCenter] = useState({
    lat: 10.4806,
    lng: -66.9036,
  });
  const [zoom, setZoom] = useState(11);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  const [mapLoadError, setMapLoadError] = useState(false);
  const [coordenadasInput, setCoordenadasInput] = useState("");
  const [direccionInput, setDireccionInput] = useState(solicitud?.direccion || "");
  const [farmaciaSeleccionada, setFarmaciaSeleccionada] = useState("");
  const [isUpdatingFarmacia, setIsUpdatingFarmacia] = useState(false);

  // Estados para plantas
  const [plantas, setPlantas] = useState([]);
  const [plantasDisponibles, setPlantasDisponibles] = useState([]);
  const [plantaSeleccionada, setPlantaSeleccionada] = useState("");
  const [entregaEnPlanta, setEntregaEnPlanta] = useState(false);
  const [loadingPlantas, setLoadingPlantas] = useState(false);
  const [modalConfirmarPlanta, setModalConfirmarPlanta] = useState(false);
  const [asignandoPlanta, setAsignandoPlanta] = useState(false);

  // Estados para flujo Mercantil
  const [estadoMercantil, setEstadoMercantil] = useState("");
  const [codigoFarmaciaMercantil, setCodigoFarmaciaMercantil] = useState("");
  const [guardandoMercantil, setGuardandoMercantil] = useState(false);

  const esMercantil = solicitud?.aseguradora === "Seguros Mercantil";

  const ESTADOS_VENEZOLANOS = [
    "Amazonas", "Anzoátegui", "Apure", "Aragua", "Barinas", "Bolívar", "Carabobo",
    "Cojedes", "Delta Amacuro", "Falcón", "Guárico", "Lara", "Mérida", "Miranda",
    "Monagas", "Nueva Esparta", "Portuguesa", "Sucre", "Táchira", "Trujillo",
    "Vargas", "Yaracuy", "Zulia", "Distrito Capital", "La Guaira"
  ];

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_APIKEYGOOGLEMAPS;

  const [borderCol1, setborderCol1] = useState(true);
  const [borderCol2, setborderCol2] = useState(false);

  const t_borderCol1 = () => {
    setborderCol1(!borderCol1);
    setborderCol2(false);
  };

  const t_borderCol2 = () => {
    setborderCol2(!borderCol2);
    setborderCol1(false);
  };

  // Función para determinar si se puede editar la dirección
  const canEditAddress = () => {
    return (
      solicitud.estatus !== "Cancelado" &&
      solicitud.estatus !== "Entregado" &&
      solicitud.estatus !== "Por Renovar"
    );
  };

  // Función para formatear la dirección con el estado en formato correcto
  const formatearDireccionConEstado = (direccionOriginal, nombreEstadoGoogle) => {
    if (!direccionOriginal) return '';

    let direccionProcesada = direccionOriginal;
    let estadoFinal = nombreEstadoGoogle;

    // 1. Si no se proveyó un nombre de estado desde Google, intentar detectarlo de la lista.
    if (!estadoFinal) {
      const estadosVenezolanos = [
        "Amazonas", "Anzoátegui", "Apure", "Aragua", "Barinas", "Bolívar", "Carabobo",
        "Cojedes", "Delta Amacuro", "Falcón", "Guárico", "Lara", "Mérida", "Miranda",
        "Monagas", "Nueva Esparta", "Portuguesa", "Sucre", "Táchira", "Trujillo",
        "Vargas", "Yaracuy", "Zulia", "Distrito Capital", "Dtto Capital", "DC", "La Guaira"
      ];
      for (const estado of estadosVenezolanos) {
        // Regex para encontrar el estado como palabra completa, opcionalmente seguido de coma y/o Venezuela
        const regex = new RegExp(`\\b${estado.replace(/\s+/g, '\\s+')}(?:,\\s*Venezuela)?$`, 'i');
        if (regex.test(direccionProcesada)) {
          estadoFinal = estado; // Usar el nombre de nuestra lista para consistencia si se detecta así
          break;
        }
      }
    }

    // 2. Limpiar "Venezuela" de la dirección original
    direccionProcesada = direccionProcesada.replace(/,\s*Venezuela$/i, '').trim();

    if (estadoFinal) {
      // Convertir el estado final a formato Tipo Título
      const estadoTitulo = estadoFinal.split(' ')
        .map(palabra => palabra.charAt(0).toUpperCase() + palabra.slice(1).toLowerCase())
        .join(' ');

      // 3. Eliminar menciones previas del estado (original o en título) y "Estado ..." de la dirección procesada
      // Esto es para evitar duplicados como "..., La Guaira, Estado La Guaira"
      // Regex para el nombre del estado (original y título), opcionalmente precedido por "Estado " y seguido por una coma al final de la subcadena.
      const regexEstadoOriginal = new RegExp(`,?\s*(?:Estado\\s+)?${estadoFinal.replace(/\s+/g, '\\s+')}(?=(?:,\s*[^,]+)*$)`, 'ig');
      const regexEstadoTitulo = new RegExp(`,?\s*(?:Estado\\s+)?${estadoTitulo.replace(/\s+/g, '\\s+')}(?=(?:,\s*[^,]+)*$)`, 'ig');
      
      direccionProcesada = direccionProcesada.replace(regexEstadoOriginal, '').trim();
      direccionProcesada = direccionProcesada.replace(regexEstadoTitulo, '').trim();
      
      // Limpieza final de comas
      direccionProcesada = direccionProcesada.replace(/,\s*,/g, ',').replace(/,+$/, '').trim();

      // 4. Añadir "Estado [Estado en Título]"
      return `${direccionProcesada}, Estado ${estadoTitulo}`;
    } else {
      // Si no se pudo determinar un estado, devolver la dirección sin Venezuela
      return direccionProcesada;
    }
  };

  
  useEffect(() => {
    try {
      // Formatear la dirección inicial con el estado
      if (solicitud && solicitud.direccion) {
        const direccionFormateada = formatearDireccionConEstado(solicitud.direccion);
        setDireccionInput(direccionFormateada);
      }
      
      if (farmacias && farmacias.length === 0) {
        setNuevaDireccion({
          direccion: "",
          coordenadas: [],
        });
      } else {
        const farmacia = farmacias.find(
          (farmacia) => farmacia.codigo_myh === solicitud.codigoProveedor
        );

        if (farmacia) {
          setNuevaDireccion({
            direccion: farmacia.direccion,
            coordenadas: farmacia.coordenadas,
          });
        } else {
          setNuevaDireccion({
            direccion: "",
            coordenadas: [],
          });
        }
      }
    } catch (error) {
      console.error("Error en useEffect:", error);
    }
  }, [solicitud, farmacias]);

  useEffect(() => {
    if (solicitud.coordenadas) {
      setCenter({
        lat: solicitud.coordenadas[0],
        lng: solicitud.coordenadas[1],
      });
      setZoom(15);
      // Inicializar el marcador con las coordenadas existentes
      setMarkerPosition({
        lat: solicitud.coordenadas[0],
        lng: solicitud.coordenadas[1],
      });
    }
  }, [solicitud]);

  useEffect(() => {
    const initGoogleMaps = async () => {
      try {
        // Cargar la API de Google Maps si no está cargada
        if (!isGoogleMapsLoaded()) {
          await loadGoogleMapsApi(GOOGLE_MAPS_API_KEY);
          // Esperar a que la API esté completamente cargada
          await waitForGoogleMapsToLoad();
        }
        
        setIsGoogleMapsReady(true);
        
        // Inicializar el geocodificador si la API está cargada
        if (window.google && window.google.maps && !geocoder) {
          setGeocoder(new window.google.maps.Geocoder());
        }
      } catch (error) {
        console.error("Error al inicializar Google Maps:", error);
        setMapLoadError(true);
        toast.error("Error al cargar el mapa. Por favor, recargue la página.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    };

    initGoogleMaps();
  }, [GOOGLE_MAPS_API_KEY, geocoder]);

  useEffect(() => {
    if (solicitud?.direccion) {
      setDireccionInput(solicitud.direccion);
    }
  }, [solicitud?.direccion]);

  // Cargar plantas y filtrar por estado (solo para aseguradoras NO Mercantil)
  useEffect(() => {
    // No cargar plantas para Seguros Mercantil
    if (solicitud?.aseguradora === "Seguros Mercantil") {
      return;
    }

    const cargarPlantas = async () => {
      setLoadingPlantas(true);
      try {
        const response = await getPlantas({ solo_activas: true });
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
          const todasPlantas = response.data.data;
          setPlantas(todasPlantas);

          // Flujo estándar: filtrar por estado extraído de la dirección
          const estadoSolicitud = extraerEstadoDeDireccion(solicitud?.direccion);
          if (estadoSolicitud) {
            // Filtrar plantas por estado
            const plantasFiltradas = todasPlantas.filter(
              planta => planta.estado.toLowerCase() === estadoSolicitud.toLowerCase()
            );
            setPlantasDisponibles(plantasFiltradas);
          } else {
            setPlantasDisponibles([]);
          }
        }
      } catch (error) {
        console.error("Error cargando plantas:", error);
        toast.error("Error al cargar plantas");
      } finally {
        setLoadingPlantas(false);
      }
    };

    if (solicitud?.direccion) {
      cargarPlantas();
    }
  }, [solicitud?.direccion, solicitud?.aseguradora]);

  // Inicializar estado de entrega en planta
  useEffect(() => {
    if (solicitud?.entregaEnPlanta?.habilitado) {
      setEntregaEnPlanta(true);
      setPlantaSeleccionada(solicitud.entregaEnPlanta.plantaId || "");
    }
  }, [solicitud?.entregaEnPlanta]);


  // Función para extraer estado de la dirección
  const extraerEstadoDeDireccion = (direccion) => {
    if (!direccion) return null;
    
    const estadosVenezolanos = [
      "Amazonas", "Anzoátegui", "Apure", "Aragua", "Barinas", "Bolívar", "Carabobo",
      "Cojedes", "Delta Amacuro", "Falcón", "Guárico", "Lara", "Mérida", "Miranda",
      "Monagas", "Nueva Esparta", "Portuguesa", "Sucre", "Táchira", "Trujillo",
      "Vargas", "Yaracuy", "Zulia", "Distrito Capital", "La Guaira"
    ];
    
    for (const estado of estadosVenezolanos) {
      const regex = new RegExp(`\\b${estado.replace(/\s+/g, '\\s+')}\\b`, 'i');
      if (regex.test(direccion)) {
        return estado;
      }
    }
    
    return null;
  };

  // Handler para cambiar checkbox de entrega en planta
  const handleEntregaEnPlantaChange = (e) => {
    const checked = e.target.checked;
    setEntregaEnPlanta(checked);
    
    if (!checked) {
      // Si se desmarca, limpiar selección
      setPlantaSeleccionada("");
    }
  };

  // Handler para cambiar planta seleccionada
  const handlePlantaChange = (e) => {
    const plantaId = e.target.value;
    setPlantaSeleccionada(plantaId);
    
    if (plantaId) {
      // Mostrar modal de confirmación
      setModalConfirmarPlanta(true);
    }
  };

  // Handler para confirmar asignación de planta
  const handleConfirmarAsignacionPlanta = async () => {
    if (!plantaSeleccionada) return;
    
    setAsignandoPlanta(true);
    try {
      const response = await asignarSolicitudAPlanta(solicitud.id, {
        planta_id: plantaSeleccionada,
        confirmado: true
      });
      
      //console.log("Response completa asignar planta:", response);
      //console.log("response.data:", response?.data);
      //console.log("response.data.data:", response?.data?.data);
      
      if (response?.data?.data) {
        // Actualizar solicitud en Redux directamente sin hacer PUT
        // Esto evita sobrescribir el campo entregaEnPlanta que acabamos de crear
        // El reducer espera action.payload.data.data, así que pasamos { data: response.data }
        dispatch(solicitudSuccess({ data: response.data }));
        
        toast.success("✓ Solicitud asignada a planta correctamente", {
          position: "top-right",
          autoClose: 3000
        });
        
        setModalConfirmarPlanta(false);
        
        // El useEffect detectará el cambio en solicitud.entregaEnPlanta y actualizará el estado local
      } else {
        console.error("No se encontró response.data.data");
        toast.error("Error: Respuesta inesperada del servidor");
      }
    } catch (error) {
      console.error("Error asignando planta:", error);
      toast.error(error?.response?.data?.detail || "Error al asignar planta");
      setPlantaSeleccionada("");
    } finally {
      setAsignandoPlanta(false);
    }
  };

  // Handler para cancelar asignación
  const handleCancelarAsignacionPlanta = () => {
    setPlantaSeleccionada(solicitud?.entregaEnPlanta?.plantaId || "");
    setModalConfirmarPlanta(false);
  };

  // Handler para remover planta
  const handleRemoverPlanta = async () => {
    if (!solicitud?.entregaEnPlanta?.habilitado) return;
    
    if (!window.confirm("¿Está seguro de remover la planta y restaurar la dirección original?")) {
      return;
    }
    
    setAsignandoPlanta(true);
    try {
      const response = await removerPlantaDeSolicitud(solicitud.id);
      
      //console.log("Response completa remover planta:", response);
      //console.log("response.data:", response?.data);
      //console.log("response.data.data:", response?.data?.data);
      
      if (response?.data?.data) {
        // Actualizar solicitud en Redux directamente sin hacer PUT
        // El reducer espera action.payload.data.data, así que pasamos { data: response.data }
        dispatch(solicitudSuccess({ data: response.data }));
        
        toast.success("✓ Planta removida y dirección restaurada", {
          position: "top-right",
          autoClose: 3000
        });
        
        // El useEffect detectará el cambio en solicitud.entregaEnPlanta y actualizará el estado local
        // Forzar actualización del estado local inmediatamente
        setEntregaEnPlanta(false);
        setPlantaSeleccionada("");
      } else {
        console.error("No se encontró response.data.data");
        toast.error("Error: Respuesta inesperada del servidor");
      }
    } catch (error) {
      console.error("Error removiendo planta:", error);
      toast.error("Error al remover planta");
    } finally {
      setAsignandoPlanta(false);
    }
  };

  const toggle = () => setModal(!modal);

  const handleDireccionChange = (e) => {
    const { value } = e.target;
    setDireccionInput(value);
  };

  const handleSubmit2 = async (direccion, coordenadas) => {
    try {
      // Asegurarse de que estamos usando la dirección formateada del input
      const direccionFinal = direccionInput || direccion;
      
      const now = new Date();
      const creado = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      ).toISOString();

      const comentario = {
        tipo: "Enviado",
        mensaje: "Actualización de la Dirección de Entrega: " + direccionFinal,
        estatus: true,
        adjunto: null,
        creado: creado,
      };

      const updateSolicitud = {
        ...solicitud,
        direccion: direccionFinal,
        coordenadas: coordenadas,
        observaciones: "Dirección de delivery actualizada",
        porcentaje_avance: 50,
        comentarios: solicitud.comentarios
          ? [
              ...(Array.isArray(solicitud.comentarios)
                ? solicitud.comentarios
                : []),
              comentario,
            ]
          : [comentario],
      };

      if (solicitud.aseguradora === "MyH+") {
        const message = {
          IdentificadorEvento: solicitud.identificador,
          Tipo: "Mensaje",
          Mensaje: "Actualización de la Dirección de Entrega: " + direccionFinal,
          Adjunto: "",
          CodigoProveedor: solicitud.codigoProveedor,
        };
        await dispatch(createMessage(message));
      }
      
      await dispatch(editSolicitud(limpiarDatosSolicitud(updateSolicitud)));

      toast.success("Dirección actualizada exitosamente", {});
      
      // Ocultar sección de actualizar dirección
      setMostrarSeccionActualizar(false);

      toggle();
    } catch (error) {
      toast.error(`Error en MyH+: ${error.message || "Error al actualizar la dirección"}`, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Asegurarse de que la dirección tiene el formato correcto con el estado
      // Si no se ha modificado direccionInput, conservamos la dirección original
      const direccionFinal = direccionInput;
      
      const now = new Date();
      const creado = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      ).toISOString();

      const comentario = {
        tipo: "Enviado",
        mensaje:
          "Actualización de la Dirección de Entrega: " +
          direccionFinal,
        estatus: true,
        adjunto: null,
        creado: creado,
      };

      const updateSolicitud = {
        ...solicitud,
        direccion: direccionFinal,
        coordenadas: nuevaDireccion.coordenadas,
        observaciones: "Dirección de delivery actualizada",
        porcentaje_avance: 50,
        comentarios: solicitud.comentarios
          ? [
              ...(Array.isArray(solicitud.comentarios)
                ? solicitud.comentarios
                : []),
              comentario,
            ]
          : [comentario],
      };

      if (solicitud.aseguradora === "MyH+") {
        const message = {
          IdentificadorEvento: solicitud.identificador,
          Tipo: "Mensaje",
          Mensaje:
            "Actualización de la Dirección de Entrega: " +
            direccionFinal,
          Adjunto: "",
          CodigoProveedor: solicitud.codigoProveedor,
        };
        await dispatch(createMessage(message));
      }
      await dispatch(editSolicitud(limpiarDatosSolicitud(updateSolicitud)));

      toast.success("Dirección actualizada exitosamente", {});
      
      // Ocultar sección de actualizar dirección
      setMostrarSeccionActualizar(false);

      toggle();
    } catch (error) {
      toast.error(`Error en MyH+: ${error.message || "Error al actualizar la dirección"}`, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para actualizar la farmacia asignada
  const handleFarmaciaChange = async (farmaciaSeleccionada) => {
    if (!farmaciaSeleccionada) return;
    
    setIsUpdatingFarmacia(true);
    try {
      const now = new Date();
      const creado = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      ).toISOString();

      const comentario = {
        tipo: "Enviado",
        mensaje: `Farmacia asignada actualizada a: ${farmaciaSeleccionada}`,
        estatus: true,
        adjunto: null,
        creado: creado,
      };

      const updateSolicitud = {
        ...solicitud,
        farmaciaAsignada: farmaciaSeleccionada,
        observaciones: "Farmacia asignada actualizada",
        comentarios: solicitud.comentarios
          ? [
              ...(Array.isArray(solicitud.comentarios)
                ? solicitud.comentarios
                : []),
              comentario,
            ]
          : [comentario],
      };

      // Solo enviar a Siscotel si es MyH+
      if (solicitud.aseguradora === "MyH+") {
        const message = {
          IdentificadorEvento: solicitud.identificador,
          Tipo: "Mensaje",
          Mensaje: `Farmacia asignada actualizada a: ${farmaciaSeleccionada}`,
          Adjunto: "",
          CodigoProveedor: solicitud.codigoProveedor,
        };
        await dispatch(createMessage(message));
      }
      await dispatch(editSolicitud(limpiarDatosSolicitud(updateSolicitud)));

      toast.success("Farmacia asignada actualizada exitosamente", {});
      setFarmaciaSeleccionada(""); // Limpiar el select
    } catch (error) {
      toast.error(`Error en MyH+: ${error.message || "Error al actualizar la farmacia"}`, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsUpdatingFarmacia(false);
    }
  };

  // Función para guardar información de Mercantil (estado + código de farmacia)
  const handleGuardarMercantil = async () => {
    if (!estadoMercantil || !codigoFarmaciaMercantil) {
      toast.error("Debe seleccionar un estado e ingresar el código de farmacia", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setGuardandoMercantil(true);
    try {
      const now = new Date();
      const creado = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      ).toISOString();

      const direccionMercantil = `Codigo de Farmacia: ${codigoFarmaciaMercantil}. Estado ${estadoMercantil}`;

      const comentario = {
        tipo: "Enviado",
        mensaje: `Información de entrega actualizada: ${direccionMercantil}`,
        estatus: true,
        adjunto: null,
        creado: creado,
      };

      const updateSolicitud = {
        ...solicitud,
        direccion: direccionMercantil,
        observaciones: "Información de entrega actualizada",
        comentarios: solicitud.comentarios
          ? [
              ...(Array.isArray(solicitud.comentarios)
                ? solicitud.comentarios
                : []),
              comentario,
            ]
          : [comentario],
      };

      await dispatch(editSolicitud(limpiarDatosSolicitud(updateSolicitud)));

      toast.success("✓ Información de entrega guardada exitosamente", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      toast.error(`Error en MyH+: ${error.message || "Error al guardar la información"}`, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setGuardandoMercantil(false);
    }
  };

  const handleMapClick = async (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    // Verificar si la ubicación está dentro de Venezuela
    if (lat >= 0.7 && lat <= 12.5 && lng >= -73.4 && lng <= -59.8) {
      try {
        // Verificar si el geocoder está inicializado
        if (!geocoder && window.google && window.google.maps) {
          setGeocoder(new window.google.maps.Geocoder());
        }
        
        // Si aún no está inicializado, crearlo directamente
        const geocoderToUse = geocoder || (window.google && window.google.maps ? new window.google.maps.Geocoder() : null);
        
        if (!geocoderToUse) {
          console.error("El geocodificador no está disponible");
          toast.error("El servicio de geocodificación no está disponible. Intente de nuevo más tarde.", {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }

        // Actualizar el marcador inmediatamente
        setMarkerPosition({ lat, lng });
        
        // Actualizar las coordenadas inmediatamente
        setNuevaDireccion(prev => ({
          ...prev,
          coordenadas: [lat, lng]
        }));

        // Intentar geocodificación inversa con manejo de errores mejorado
        let addressFound = false;
        let retryCount = 0;
        const maxRetries = 2;
        
        while (!addressFound && retryCount <= maxRetries) {
          try {
            // Añadir un pequeño retraso entre intentos
            if (retryCount > 0) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
            
            const response = await new Promise((resolve, reject) => {
              geocoderToUse.geocode(
                { location: { lat, lng } },
                (results, status) => {
                  if (status === "OK") {
                    resolve(results);
                  } else {
                    reject(new Error(`GEOCODER_GEOCODE: ${status}`));
                  }
                }
              );
            });
            
            if (response && response.length > 0) {
              // Extraer el estado de los resultados de geocodificación
              const formattedAddress = response[0].formatted_address;
              let estado = "";
              
              // Buscar el componente de tipo administrative_area_level_1 (estado/provincia)
              if (response[0].address_components) {
                for (const component of response[0].address_components) {
                  if (component.types.includes("administrative_area_level_1")) {
                    estado = component.long_name;
                    break;
                  }
                }
              }
              
              // Formatear la dirección incluyendo el estado explícitamente
              let direccionConEstado = formattedAddress;
              if (estado) {
                // Convertir el estado a formato Tipo Título (primera letra de cada palabra en mayúscula)
                const estadoTitulo = estado.split(' ')
                  .map(palabra => palabra.charAt(0).toUpperCase() + palabra.slice(1).toLowerCase())
                  .join(' ');
                
                // Eliminar cualquier mención existente del estado
                direccionConEstado = direccionConEstado
                  .replace(new RegExp(`Estado\s+${estado}`, 'i'), '')
                  .replace(new RegExp(`Estado\s+${estadoTitulo}`, 'i'), '')
                  .replace(new RegExp(`${estado},\s*Venezuela`, 'i'), 'Venezuela')
                  .replace(new RegExp(`${estadoTitulo},\s*Venezuela`, 'i'), 'Venezuela')
                  .replace(/,\s*,/g, ',');
                
                // Agregar el estado en el formato correcto
                direccionConEstado = direccionConEstado.replace(/,\s*Venezuela/, `, Estado ${estadoTitulo}, Venezuela`);
                
                // Si no tiene Venezuela al final, agregarlo antes del final
                if (!direccionConEstado.includes('Venezuela')) {
                  direccionConEstado = direccionConEstado.trim();
                  if (place && place.formatted_address && place.geometry && place.geometry.location) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();

                    // Validar si está dentro de Venezuela
                    if (lat >= 0.7 && lat <= 12.5 && lng >= -73.4 && lng <= -59.8) {
                      let nombreEstadoGoogle = "";
                      if (place.address_components) {
                        const adminLevel1 = place.address_components.find(comp => comp.types.includes("administrative_area_level_1"));
                        if (adminLevel1) {
                          nombreEstadoGoogle = adminLevel1.long_name;
                        }
                      }

                      const direccionFormateada = formatearDireccionConEstado(place.formatted_address, nombreEstadoGoogle);

                      setDireccionInput(direccionFormateada); // Actualiza el campo de texto inferior
                      setMapCenter({ lat, lng });
                      setMarkerPosition({ lat, lng });
                      setNuevaDireccion({
                        direccion: direccionFormateada,
                        coordenadas: [lat, lng],
                      });
                    } else {
                      toast.error("La ubicación seleccionada está fuera de Venezuela.");
                      // Considerar revertir direccionInput al valor anterior o al de solicitud.direccion
                      // setDireccionInput(solicitud.direccion || ''); 
                    }
                  } else {
                    // Autocomplete: place, formatted_address, o geometry no definidos
                  }
                }
              }
              setDireccionInput(direccionConEstado);
              addressFound = true;
            } else {
              throw new Error("No se encontraron resultados de geocodificación");
            }
          } catch (geocodeError) {
            retryCount++;
            
            // Si es el último intento y aún falla, usamos las coordenadas directamente
            if (retryCount > maxRetries) {
              toast.warning(`Error al obtener la dirección: ${geocodeError.message}. Se usarán las coordenadas directamente.`, {
                position: "top-right",
                autoClose: 5000,
              });
              setDireccionInput(`Ubicación: ${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            }
          }
        }
      } catch (error) {
        console.error("Error general al procesar la ubicación:", error);
        toast.error("Error al procesar la ubicación seleccionada. Por favor, intente de nuevo.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } else {
      toast.warning("Por favor, seleccione una ubicación dentro de Venezuela.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleGuardarDireccion = async () => {
    setIsSubmitting(true);
    try {
      // Preparar los datos para enviar
      const datosActualizados = {
        ...solicitud,
        direccion: direccionInput,
        coordenadas: nuevaDireccion.coordenadas,
      };

      // Enviar los datos actualizados
      await dispatch(editSolicitud(limpiarDatosSolicitud(datosActualizados)));

      // Mostrar mensaje de éxito
      await dispatch(
        createMessage({
          message: "Dirección actualizada con éxito",
          type: "success",
        })
      );
      
      // Ocultar sección de actualizar dirección
      setMostrarSeccionActualizar(false);

      // Cerrar el modal
      toggle();
    } catch (error) {
      console.error("Error al actualizar la dirección:", error);
      dispatch(
        createMessage({
          message: "Error al actualizar la dirección",
          type: "error",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="delivery-info-card">
            <CardHeader>
              <div className="d-flex align-items-center">
                <i className="ri-truck-line me-1 text-primary me-2 fs-20"></i>
                <h5 className="card-title mb-0">
                  {esMercantil ? "Información para la Entrega" : "Información para el Delivery"}
                </h5>
              </div>
            </CardHeader>

            <CardBody>
              <div className="table-responsive table-card">
                <Table className="table-borderless align-middle mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-medium" width="40%">
                        <i className="ri-user-line text-muted me-2"></i>Farmacia Asignada
                      </td>
                      <td id="d-date" className="text-break">
                        {esMercantil ? (
                          solicitud.entregaEnPlanta?.habilitado
                            ? <span>{solicitud.entregaEnPlanta.plantaNombre}</span>
                            : (solicitud.farmaciaAsignada || <span className="text-muted fst-italic">Pendiente de asignación</span>)
                        ) : solicitud.codigoProveedor === "00083" ? (
                          !solicitud.farmaciaAsignada ? (
                            <div className="d-flex align-items-center gap-2">
                              <Input
                                type="select"
                                value={farmaciaSeleccionada}
                                onChange={(e) => setFarmaciaSeleccionada(e.target.value)}
                                disabled={isUpdatingFarmacia}
                                className="form-select-sm"
                                style={{ minWidth: "200px" }}
                              >
                                <option value="">Seleccionar farmacia...</option>
                                <option value="FSI San Joaquin">FSI San Joaquin</option>
                                <option value="FSI Naguanagua">FSI Naguanagua</option>
                                <option value="FSI Camoruco">FSI Camoruco</option>
                                <option value="FSI La Salud">FSI La Salud</option>
                                <option value="FSI Barquisimeto">FSI Barquisimeto</option>
                              </Input>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => handleFarmaciaChange(farmaciaSeleccionada)}
                                disabled={!farmaciaSeleccionada || isUpdatingFarmacia}
                                className="btn-sm"
                                title="Asignar farmacia"
                              >
                                {isUpdatingFarmacia ? (
                                  <i className="ri-loader-line"></i>
                                ) : (
                                  <i className="ri-check-line"></i>
                                )}
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-2">
                              <span>{solicitud.farmaciaAsignada}</span>
                              <Button
                                color="warning"
                                size="sm"
                                onClick={() => {
                                  setFarmaciaSeleccionada("");
                                  const updateSolicitud = {
                                    ...solicitud,
                                    farmaciaAsignada: null,
                                    observaciones: "Farmacia asignada removida para reasignación",
                                  };
                                  dispatch(editSolicitud(limpiarDatosSolicitud(updateSolicitud)));
                                  toast.success("Farmacia removida. Ahora puede seleccionar una nueva.", {});
                                }}
                                disabled={isUpdatingFarmacia}
                                className="btn-sm"
                                title="Editar farmacia asignada"
                              >
                                <i className="ri-edit-line"></i>
                              </Button>
                            </div>
                          )
                        ) : (
                          solicitud.farmaciaAsignada || "-"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium" width="40%">
                        <i className="ri-user-line text-muted me-2"></i>Recibe
                      </td>
                      <td id="d-date" className="text-break">
                        {solicitud.recibe || "-"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium">
                        <i className="ri-phone-line text-muted me-2"></i>
                        Teléfono
                      </td>
                      <td id="d-date">
                        {solicitud.telefono ? (
                          <a
                            href={`tel:${solicitud.telefono}`}
                            className="text-primary"
                          >
                            {solicitud.telefono}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                    {/* <tr>
                      <td className="fw-medium">
                        <i className="ri-map-pin-line text-muted me-2"></i>
                        Estado
                      </td>
                      <td id="d-date">{nuevaDireccion.direccion ? nuevaDireccion.direccion : "-"}</td>
                    </tr> */}
                   
                    <tr>
                      <td className="fw-medium">
                        <i className="ri-road-map-line text-muted me-2"></i>
                        Dirección
                      </td>
                      <td id="d-date" className="text-break">
                        {direccionInput || solicitud.direccion || "-"}
                      </td>
                    </tr>
                    
                    
                    {/* Información de Entrega para Mercantil */}
                    {esMercantil ? (
                      <tr>
                        <td className="fw-medium">
                          <i className="ri-store-2-line text-muted me-2"></i>
                          Información de Entrega
                        </td>
                        <td id="d-date">
                          <Row className="g-3">
                            <Col md={6}>
                              <Label className="form-label fw-semibold mb-1">
                                <i className="ri-map-pin-line me-1"></i>Estado
                              </Label>
                              <Input
                                type="select"
                                value={estadoMercantil}
                                onChange={(e) => setEstadoMercantil(e.target.value)}
                                disabled={guardandoMercantil}
                                className="form-select"
                              >
                                <option value="">-- Seleccione estado --</option>
                                {ESTADOS_VENEZOLANOS.map((estado) => (
                                  <option key={estado} value={estado}>{estado}</option>
                                ))}
                              </Input>
                            </Col>
                            <Col md={6}>
                              <Label className="form-label fw-semibold mb-1">
                                <i className="ri-barcode-line me-1"></i>Código de Farmacia
                              </Label>
                              <Input
                                type="text"
                                value={codigoFarmaciaMercantil}
                                onChange={(e) => setCodigoFarmaciaMercantil(e.target.value)}
                                disabled={guardandoMercantil}
                                placeholder="Ingrese código de farmacia"
                                className="form-control"
                              />
                            </Col>
                            <Col md={12}>
                              <Button
                                color="primary"
                                onClick={handleGuardarMercantil}
                                disabled={guardandoMercantil || !estadoMercantil || !codigoFarmaciaMercantil}
                                className="w-100"
                              >
                                {guardandoMercantil ? (
                                  <><Spinner size="sm" className="me-2" /> Guardando...</>
                                ) : (
                                  <><i className="ri-save-line me-2"></i> Guardar Información de Entrega</>
                                )}
                              </Button>
                            </Col>
                          </Row>
                        </td>
                      </tr>
                    ) : (
                      /* Flujo estándar: Entrega en Planta gestionado por coordenadas */
                      solicitud?.coordenadas && solicitud.coordenadas.length === 2 && (
                        <tr>
                          <td className="fw-medium">
                            <i className="ri-building-line text-muted me-2"></i>
                            Entrega en Planta
                          </td>
                          <td id="d-date">
                            <div className="d-flex align-items-center gap-3">
                              <FormGroup check className="mb-0 form-check-success">
                                <Input
                                  type="checkbox"
                                  id="entregaEnPlanta"
                                  checked={entregaEnPlanta}
                                  onChange={handleEntregaEnPlantaChange}
                                  disabled={!plantasDisponibles.length || loadingPlantas || asignandoPlanta}
                                  className="form-check-input"
                                />
                                <Label check for="entregaEnPlanta" className="mb-0 ms-2 fw-medium">
                                  {loadingPlantas ? (
                                    <span className="text-muted">
                                      <Spinner size="sm" className="me-1" />
                                      Cargando plantas...
                                    </span>
                                  ) : (
                                    "Habilitar entrega en planta"
                                  )}
                                </Label>
                              </FormGroup>
                            </div>

                            {entregaEnPlanta && (
                              <div className="mt-3">
                                {plantasDisponibles.length > 0 ? (
                                  <>
                                    <Label for="plantaSelect" className="form-label fw-semibold mb-2">
                                      <i className="ri-map-pin-line me-1"></i>
                                      Seleccione la planta de entrega:
                                    </Label>
                                    <Input
                                      type="select"
                                      id="plantaSelect"
                                      value={plantaSeleccionada}
                                      onChange={handlePlantaChange}
                                      disabled={asignandoPlanta}
                                      className="form-select"
                                    >
                                      <option value="">-- Seleccione una planta --</option>
                                      {plantasDisponibles.map((planta) => (
                                        <option key={planta.id} value={planta.id}>
                                          📍 {planta.nombre} - {planta.direccion}
                                        </option>
                                      ))}
                                    </Input>

                                    {solicitud?.entregaEnPlanta?.habilitado && (
                                      <div className="alert alert-info mt-2 mb-0 py-2">
                                        <div className="d-flex align-items-start">
                                          <i className="ri-information-line me-2 fs-16"></i>
                                          <div className="flex-grow-1">
                                            <strong className="d-block">{solicitud.entregaEnPlanta.plantaNombre}</strong>
                                            <small className="text-muted d-block">
                                              Asignado por {solicitud.entregaEnPlanta.asignadoPor} el{" "}
                                              {new Date(solicitud.entregaEnPlanta.fechaAsignacion).toLocaleDateString()}
                                            </small>
                                            <Button
                                              color="warning"
                                              size="sm"
                                              onClick={handleRemoverPlanta}
                                              disabled={asignandoPlanta}
                                              className="mt-2"
                                            >
                                              {asignandoPlanta ? (
                                                <><Spinner size="sm" className="me-1" /> Procesando...</>
                                              ) : (
                                                <><i className="ri-close-line me-1"></i> Remover Planta</>
                                              )}
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="alert alert-warning mt-2 mb-0 py-2">
                                    <i className="ri-alert-line me-2"></i>
                                    <small>No hay plantas disponibles para el estado de esta dirección.</small>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    )}
                    
                    {!esMercantil && (
                    <tr>
                      <td className="fw-medium">
                        <i className="ri-map-2-line text-muted me-2"></i>
                        Coordenadas
                      </td>
                      <td id="d-date">
                        {/* Si hay coordenadas en nuevaDireccion, mostrarlas (previsualización antes de guardar) */}
                        {nuevaDireccion.coordenadas && nuevaDireccion.coordenadas.length === 2 ? (
                          <>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${nuevaDireccion.coordenadas[0]},${nuevaDireccion.coordenadas[1]}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-soft-primary"
                            >
                              <i className="ri-map-pin-line me-1"></i>
                              {`${nuevaDireccion.coordenadas[0].toFixed(
                                6
                              )}, ${nuevaDireccion.coordenadas[1].toFixed(6)}`}
                            </a>
                            {/* Pequeño indicador de que hay cambios sin guardar */}
                            <span className="ms-2 badge bg-soft-warning text-warning">Sin guardar</span>
                          </>
                        ) : solicitud.coordenadas ? (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${solicitud.coordenadas[0]},${solicitud.coordenadas[1]}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-soft-primary"
                          >
                            <i className="ri-map-pin-line me-1"></i>
                            {`${solicitud.coordenadas[0].toFixed(
                              6
                            )}, ${solicitud.coordenadas[1].toFixed(6)}`}
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                    )}
                    <tr>
                      <td className="fw-medium">
                        <i className="ri-file-text-line text-muted me-2"></i>
                        Observaciones
                      </td>
                      <td id="d-date" className="text-break">
                        {solicitud.observaciones || "-"}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {/* Opción para actualizar dirección - solo para flujo no-Mercantil */}
              {!esMercantil && canEditAddress() && (
                <div className="mt-3">
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    onClick={() => {
                      setMostrarSeccionActualizar(true);
                      // Scroll hacia la sección de actualizar dirección
                      setTimeout(() => {
                        const element = document.querySelector('.update-address-card');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 100);
                    }}
                    className="d-flex align-items-center"
                  >
                    <i className="ri-edit-line me-1"></i>
                    Actualizar dirección de entrega
                  </Button>
                </div>
              )}

              {solicitud.coordenadas ? (
                <div className="map-preview mt-4">
                  <h6 className="mb-2">Vista previa de ubicación</h6>
                  <div
                    className="border rounded overflow-hidden"
                    style={{ height: "200px" }}
                  >
                    <GoogleMapWrapper
                      apiKey={GOOGLE_MAPS_API_KEY}
                      center={{
                        lat: solicitud.coordenadas[0],
                        lng: solicitud.coordenadas[1],
                      }}
                      zoom={15}
                      markerPosition={{
                        lat: solicitud.coordenadas[0],
                        lng: solicitud.coordenadas[1],
                      }}
                      onMapLoad={(map) => {
                        setMap(map);
                        if (window.google && window.google.maps) {
                          setGeocoder(new window.google.maps.Geocoder());
                        }
                      }}
                      height="200px"
                    />
                  </div>
                </div>
              ) : !esMercantil ? (
                <div className="mt-4">
                  <div className="alert alert-warning">
                    <div className="d-flex align-items-center">
                      <i className="ri-map-pin-line fs-20 me-2"></i>
                      <div>
                        <h6 className="mb-1">No hay ubicación registrada</h6>
                        <p className="mb-0">
                          Debe usar <strong>"Actualizar Dirección de Entrega"</strong> para poder continuar.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Sección de Verificación de Dirección - solo para flujo no-Mercantil */}
              {!esMercantil && solicitud?.coordenadas && solicitud.coordenadas.length === 2 && (
                <div className="mt-4">
                  {direccionVerificada ? (
                    // Estado: Verificada
                    <div className="d-flex align-items-center justify-content-between p-3 bg-success-subtle rounded border border-success-subtle">
                      <div className="d-flex align-items-center">
                        <div className="avatar-xs me-3">
                          <div className="avatar-title bg-success text-white rounded-circle">
                            <i className="ri-checkbox-circle-fill fs-16"></i>
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-0 text-success">Dirección Verificada</h6>
                          <small className="text-muted">
                            Por: {verificadoPor} • {formatearFechaVerificacion(fechaVerificacionDireccion)}
                          </small>
                        </div>
                      </div>
                      <span className="badge bg-success">
                        <i className="ri-checkbox-circle-line me-1"></i>
                        Verificada
                      </span>
                    </div>
                  ) : !puedeVerificarDireccion ? (
                    // Estado: Bloqueado - Faltan pasos previos
                    <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded border">
                      <div className="d-flex align-items-center">
                        <div className="avatar-xs me-3">
                          <div className="avatar-title bg-secondary text-white rounded-circle">
                            <i className="ri-lock-line fs-16"></i>
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-0 text-secondary">Verificación Bloqueada</h6>
                          <small className="text-muted">
                            Complete: 
                            {tipoOp === "Cronica" 
                              ? [
                                  !documentosVerificados && "Verificar documentos",
                                  !tieneMedicamentos && "Medicamentos",
                                  !tieneFactura && "Facturación",
                                  permisoExcedentePendiente && "Permiso Mercantil (monto excedente)"
                                ].filter(Boolean).join(", ")
                              : [
                                  !documentosVerificados && "Verificar documentos",
                                  !tieneDiagnostico && "Diagnóstico",
                                  !tieneFactura && "Facturación",
                                  permisoExcedentePendiente && "Permiso Mercantil (monto excedente)"
                                ].filter(Boolean).join(", ")
                            }
                          </small>
                        </div>
                      </div>
                      <span className="badge bg-secondary">
                        <i className="ri-lock-line me-1"></i>
                        Bloqueado
                      </span>
                    </div>
                  ) : (
                    // Estado: Pendiente de verificación
                    <div className="d-flex align-items-center justify-content-between p-3 bg-warning-subtle rounded border border-warning-subtle">
                      <div className="d-flex align-items-center">
                        <div className="avatar-xs me-3">
                          <div className="avatar-title bg-warning text-dark rounded-circle">
                            <i className="ri-map-pin-time-line fs-16"></i>
                          </div>
                        </div>
                        <div>
                          <h6 className="mb-0 text-warning">Pendiente de Verificación</h6>
                          <small className="text-muted">
                            Confirme que la dirección y coordenadas son correctas
                          </small>
                        </div>
                      </div>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleVerificarDireccion}
                        disabled={verificandoDireccion || solicitud?.estatus === "Cancelado" || solicitud?.estatus === "Entregado"}
                        className="d-flex align-items-center"
                      >
                        {verificandoDireccion ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Verificando...
                          </>
                        ) : (
                          <>
                            <i className="ri-checkbox-circle-line me-1"></i>
                            Confirmar Dirección
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        {/* Sección "Actualizar Dirección de Entrega" - disponible para todas las aseguradoras */}
        {mostrarSeccionActualizar  && (
          <Col lg={12}>
            <Card className="update-address-card">
            <CardHeader>
              <div className="d-flex align-items-center">
                <i className="ri-truck-line me-1 text-primary me-2 fs-20"></i>
                <h5 className="card-title mb-0">
                  Actualizar Dirección de Entrega
                </h5>
              </div>
            </CardHeader>

            <CardBody>
              <div className="address-options">
                <div className="d-flex mb-3">
                  <button
                    className={`btn ${
                      borderCol1 ? "btn-primary" : "btn-light"
                    } me-2 flex-grow-1`}
                    onClick={t_borderCol1}
                    type="button"
                  >
                    <i className="ri-map-pin-line me-1"></i> Usar mapa
                  </button>
                  <button
                    className={`btn ${
                      borderCol2 ? "btn-primary" : "btn-light"
                    } flex-grow-1`}
                    onClick={t_borderCol2}
                    type="button"
                  >
                    <i className="ri-compass-3-line me-1"></i> Usar
                    coordenadas
                  </button>
                </div>

                <div className="address-content p-3 border rounded">
                  {borderCol1 && (
                    <div className="map-option">
                      <p className="text-muted mb-3">
                        Seleccione una ubicación en el mapa para actualizar la
                        dirección de entrega.
                      </p>
                      {!canEditAddress() ? (
                        <div className="alert alert-info">
                          <i className="ri-information-line me-1"></i>
                          No se puede modificar la dirección en el estado
                          actual.
                        </div>
                      ) : (
                        <Button
                          color="primary"
                          className="w-100"
                          onClick={toggle}
                        >
                          <i className="ri-map-pin-add-line me-1"></i> Abrir
                          mapa
                        </Button>
                      )}
                    </div>
                  )}

                  {borderCol2 && (
                    <div className="coordinates-option">
                      {!canEditAddress() ? (
                        <div className="alert alert-info">
                          <i className="ri-information-line me-1"></i>
                          No se puede modificar la dirección en el estado
                          actual.
                        </div>
                      ) : (
                        <div>
                          <FormGroup>
                            <Label>
                              <i className="ri-map-pin-line me-1"></i>
                              Coordenadas (latitud, longitud)
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              name="coordenadasInput"
                              value={coordenadasInput || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Actualizar el estado local para permitir la escritura
                                setCoordenadasInput(value);
                                
                                // Intentar extraer coordenadas del formato "lat, lng"
                                const coordsMatch = value.match(/^\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*$/);
                                if (coordsMatch) {
                                  const lat = parseFloat(coordsMatch[1]);
                                  const lng = parseFloat(coordsMatch[2]);
                                  setNuevaDireccion(prev => ({
                                    ...prev,
                                    coordenadas: [lat, lng]
                                  }));
                                  
                                  // Actualizar el centro del mapa y el marcador
                                  setCenter({
                                    lat: lat,
                                    lng: lng,
                                  });
                                  
                                  // Actualizar el marcador en el mapa
                                  setMarkerPosition({
                                    lat: lat,
                                    lng: lng
                                  });
                                  
                                  // Ajustar el zoom para ver mejor la ubicación
                                  setZoom(15);
                                  
                                  // Intentar obtener la dirección a partir de las coordenadas
                                  if (geocoder) {
                                    geocoder.geocode(
                                      { location: { lat, lng } },
                                      (results, status) => {
                                        if (status === "OK" && results[0]) {
                                          // Extraer el estado de los resultados de geocodificación
                                          const formattedAddress = results[0].formatted_address;
                                          let estado = "";
                                          
                                          // Buscar el componente de tipo administrative_area_level_1 (estado/provincia)
                                          if (results[0].address_components) {
                                            for (const component of results[0].address_components) {
                                              if (component.types.includes("administrative_area_level_1")) {
                                                estado = component.long_name;
                                                break;
                                              }
                                            }
                                          }
                                          
                                          // Usar la función universal de formateo
                                          const direccionFormateada = formatearDireccionConEstado(formattedAddress, estado);
                                          
                                          // Actualizar tanto direccionInput como nuevaDireccion
                                          setDireccionInput(direccionFormateada);
                                          setNuevaDireccion(prev => ({
                                            ...prev,
                                            direccion: direccionFormateada
                                          }));
                                        }
                                      }
                                    );
                                  }
                                }
                              }}
                              placeholder="Ejemplo: 10.4806, -66.9036"
                            />
                            <small className="text-muted">
                              Ingrese las coordenadas en formato decimal separadas por coma (latitud, longitud)
                            </small>
                          </FormGroup>
                          <FormGroup>
                            <Label>
                              <i className="ri-map-pin-line me-1"></i>
                              Dirección
                            </Label>
                            <Input
                              type="text"
                              className="form-control"
                              name="direccion"
                              value={direccionInput || ""}
                              onChange={(e) => {
                                setDireccionInput(e.target.value);
                              }}
                              placeholder="La dirección se completará automáticamente al ingresar coordenadas válidas"
                            />
                          </FormGroup>
                          <Button
                            color="primary"
                            className="w-100"
                            onClick={() =>
                              handleSubmit2(
                                direccionInput,
                                nuevaDireccion.coordenadas
                              )
                            }
                          >
                            <i className="ri-map-pin-add-line me-1"></i> Guardar
                            cambios
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
        )}
      </Row>

      <Modal
        isOpen={modal}
        toggle={toggle}
        size="xl"
        className="address-update-modal"
      >
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={toggle} className="bg-light">
            <i className="ri-map-pin-line me-2 text-primary"></i>
            Editar Dirección de Entrega
          </ModalHeader>
          <ModalBody>
            <div className="alert alert-info mb-3">
              <i className="ri-information-line me-2"></i>
              Seleccione una ubicación en el mapa o busque una dirección para
              actualizar la información de entrega.
            </div>

            <Row>
              <Col md={12} className="mb-3">
                <FormGroup className="mb-3 search-box">
                  <Label>
                    <i className="ri-search-line me-1"></i>
                    Buscar dirección
                  </Label>
                  {isGoogleMapsReady ? (
                    <Autocomplete
                      onLoad={(autocomplete) => {
                        setSearchBox(autocomplete);
                      }}
                      onPlaceChanged={() => {
                        try {
                          if (searchBox && typeof searchBox.getPlace === "function") {
                            const place = searchBox.getPlace();
                            if (place && place.geometry) {
                              const lat = place.geometry.location.lat();
                              const lng = place.geometry.location.lng();
                              if (
                                lat >= 0.7 &&
                                lat <= 12.5 &&
                                lng >= -73.4 &&
                                lng <= -59.8
                              ) {
                                let nombreEstadoGoogle = "";
                                if (place.address_components) {
                                  const adminLevel1 = place.address_components.find(comp => comp.types.includes("administrative_area_level_1"));
                                  if (adminLevel1) {
                                    nombreEstadoGoogle = adminLevel1.long_name;
                                  }
                                }

                                const direccionFormateada = formatearDireccionConEstado(place.formatted_address, nombreEstadoGoogle);

                                setDireccionInput(direccionFormateada); // ACTUALIZA EL TEXTO VISIBLE (3)
                                setCenter({ lat: lat, lng: lng }); // Revertido para centrar el mapa
                                setMarkerPosition({ lat, lng }); // Mueve el marcador
                                setNuevaDireccion({
                                  direccion: direccionFormateada, // Asegura que se guarda la formateada
                                  coordenadas: [lat, lng],
                                });
                                
                                // Actualizar el centro del mapa (esto podría ser redundante si setMapCenter ya lo hace)
                                // setCenter({
                                //   lat: lat,
                                //   lng: lng,
                                // });

                                // Ajustar el zoom para ver mejor la ubicación
                                setZoom(15);
                                
                                // Actualizar el marcador en el mapa
                                setMarkerPosition({
                                  lat: lat,
                                  lng: lng
                                });
                                
                                // Mostrar mensaje de confirmación
                                toast.success("Ubicación seleccionada correctamente", {
                                  position: "top-right",
                                  autoClose: 2000,
                                });
                              } else {
                                toast.error(
                                  "Por favor, seleccione una ubicación dentro de Venezuela",
                                  {
                                    position: "top-right",
                                    autoClose: 3000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                  }
                                );
                              }
                            }
                          } else {
                            console.error("searchBox no está definido o getPlace no es una función");
                          }
                        } catch (error) {
                          console.error("Error en onPlaceChanged:", error);
                        }
                      }}
                      options={{
                        componentRestrictions: { country: "ve" },
                        types: ["geocode", "establishment"],
                        fields: [
                          "formatted_address",
                          "geometry",
                          "name",
                          "place_id",
                        ],
                        strictBounds: true,
                        bounds: {
                          north: 12.5,
                          south: 0.7,
                          east: -59.8,
                          west: -73.4,
                        },
                      }}
                    >
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="Buscar dirección en Venezuela..."
                      />
                    </Autocomplete>
                  ) : (
                    <div>
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="Cargando búsqueda de direcciones..."
                        disabled
                      />
                      <div className="mt-2 text-center">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <small className="ms-2 text-muted">Cargando componente de búsqueda...</small>
                      </div>
                    </div>
                  )}
                  <small className="text-muted">
                    Escriba una dirección y seleccione una opción del menú
                    desplegable
                  </small>
                </FormGroup>

                <div
                  className="map-container border rounded overflow-hidden mb-3"
                  style={{ height: "400px" }}
                >
                  {mapLoadError ? (
                    <div className="text-center p-3 bg-light rounded h-100 d-flex flex-column justify-content-center">
                      <p className="text-danger mb-2">
                        <i className="ri-error-warning-line me-1"></i>
                        Error al cargar el mapa de Google
                      </p>
                      <Button color="primary" size="sm" onClick={() => window.location.reload()}>
                        <i className="ri-refresh-line me-1"></i>
                        Recargar página
                      </Button>
                    </div>
                  ) : (
                    <GoogleMapWrapper
                      apiKey={GOOGLE_MAPS_API_KEY}
                      center={center}
                      zoom={zoom}
                      markerPosition={markerPosition}
                      onClick={handleMapClick}
                      onMapLoad={(map) => {
                        setMap(map);
                        if (window.google && window.google.maps && !geocoder) {
                          setGeocoder(new window.google.maps.Geocoder());
                        }
                      }}
                      height="400px"
                    />
                  )}
                </div>
              </Col>
            </Row>
            <div className="address-preview p-3 border rounded mb-3">
              <h6 className="mb-2">Dirección seleccionada</h6>
              <p className="mb-1">
                <strong>Dirección:</strong>{" "}
                {direccionInput || "No seleccionada"}
              </p>
              <p className="mb-0">
                <strong>Coordenadas:</strong>{" "}
                {nuevaDireccion.coordenadas && 
                 Array.isArray(nuevaDireccion.coordenadas) && 
                 nuevaDireccion.coordenadas.length >= 2 &&
                 nuevaDireccion.coordenadas[0] !== undefined &&
                 nuevaDireccion.coordenadas[1] !== undefined
                  ? `${nuevaDireccion.coordenadas[0].toFixed(
                      6
                    )}, ${nuevaDireccion.coordenadas[1].toFixed(6)}`
                  : "No seleccionadas"}
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={toggle}>
              Cancelar
            </Button>{" "}
            <Button
              color="primary"
              type="submit"
              disabled={
                isSubmitting ||
                !nuevaDireccion.direccion ||
                !nuevaDireccion.coordenadas
              }
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Actualizando...
                </>
              ) : (
                <>Guardar cambios</>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Modal de Confirmación para Asignar Planta */}
      <Modal isOpen={modalConfirmarPlanta} toggle={handleCancelarAsignacionPlanta} centered>
        <ModalHeader toggle={handleCancelarAsignacionPlanta}>
          <i className="ri-alert-line text-warning me-2"></i>
          Confirmar Asignación de Planta
        </ModalHeader>
        <ModalBody>
          {plantaSeleccionada && (
            <>
              <div className="alert alert-warning">
                <i className="ri-information-line me-2"></i>
                <strong>Importante:</strong> Al asignar esta solicitud a una planta, la dirección y coordenadas 
                del titular serán reemplazadas por las de la planta seleccionada.
              </div>

              <div className="mb-3">
                <h6 className="mb-2">Planta seleccionada:</h6>
                {(() => {
                  const planta = plantasDisponibles.find(p => p.id === plantaSeleccionada);
                  if (!planta) return null;
                  
                  return (
                    <div className="p-3 bg-light rounded">
                      <div className="mb-2">
                        <strong>{planta.nombre}</strong>
                      </div>
                      <div className="mb-2">
                        <i className="ri-map-pin-line me-1 text-muted"></i>
                        <small>{planta.direccion}</small>
                      </div>
                      {Array.isArray(planta.contactos) && planta.contactos.length > 0 && (
                        <div>
                          <small className="text-muted">Contactos:</small>
                          {planta.contactos.map((contacto, idx) => (
                            <div key={idx} className="ms-2">
                              <small>
                                <i className="ri-user-line me-1"></i>
                                {contacto.nombre} - {contacto.telefono}
                              </small>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="mb-3">
                <h6 className="mb-2">Dirección original (se guardará como respaldo):</h6>
                <div className="p-2 bg-light rounded">
                  <small className="text-muted">{solicitud.direccion}</small>
                </div>
              </div>

              <p className="mb-0">
                ¿Está seguro de que desea continuar con esta asignación?
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={handleCancelarAsignacionPlanta}
            disabled={asignandoPlanta}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmarAsignacionPlanta}
            disabled={asignandoPlanta}
          >
            {asignandoPlanta ? (
              <>
                <Spinner size="sm" className="me-1" />
                Asignando...
              </>
            ) : (
              <>
                <i className="ri-check-line me-1"></i>
                Confirmar Asignación
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

/* */
export default InformacionDelivery;
